import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FloppyDisk, Camera, Trash, Star, Plus } from "@phosphor-icons/react";
import { ProfilePhoto, photoUrl } from "@/components/AuthImage";

const emptyProfile = {
  name: "", dob: "", height_cm: "", marital_status: "",
  community: "", sub_sect: "", mother_tongue: "",
  country: "", state: "", city: "",
  education: "", profession: "", annual_income: "",
  diet: "", about_me: "",
  namaz: "", observance: "", reads_quran: "",
  family_status: "", family_type: "", father_occupation: "", mother_occupation: "", siblings: "",
  partner_age_min: "", partner_age_max: "", partner_height_min_cm: "",
  partner_education: "", partner_community: "", partner_expectations: "",
};

const SECTS = ["Sunni", "Shia", "Sufi", "Bohra", "Ismaili", "Ahmadiyya", "Other"];
const MOTHER_TONGUES = ["Urdu", "Arabic", "Hindi", "English", "Bengali", "Pashto", "Farsi", "Malayalam", "Tamil", "Kashmiri", "Gujarati", "Marathi", "Other"];
const DIET = ["Halal", "Vegetarian", "Vegan", "Non-halal (avoid)", "Halal + Vegetarian"];
const NAMAZ = ["Five times daily", "Regularly", "Sometimes", "Rarely"];

export default function MyProfile() {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/profiles/me");
    setProfile(data);
    setForm({
      ...emptyProfile,
      ...data,
      height_cm: data.height_cm || "",
      partner_age_min: data.partner_age_min || "",
      partner_age_max: data.partner_age_max || "",
      partner_height_min_cm: data.partner_height_min_cm || "",
    });
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — please choose a file under 8MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded — jazakAllah khair.");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo from your dossier?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo removed.");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const makePrimary = async (photoId) => {
    try {
      await api.post(`/photos/${photoId}/primary`);
      toast.success("Primary photo updated.");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["height_cm", "partner_age_min", "partner_age_max", "partner_height_min_cm"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else payload[k] = parseInt(payload[k], 10);
      });
      await api.put("/profiles/me", payload);
      toast.success("Dossier updated, jazak-Allah khair");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-brand-muted">Loading…</div>;

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid="my-profile-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-brand-terracotta">Your dossier</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-2 text-brand-ink">My profile</h1>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-6">
          <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Tabs defaultValue="photos" className="mt-10">
        <TabsList className="bg-transparent border-b border-brand-line rounded-none p-0 w-full justify-start gap-6 overflow-x-auto">
          {["photos", "basic", "deen", "family", "career", "partner"].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              data-testid={`edit-tab-${t}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow whitespace-nowrap"
            >
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="photos" className="pt-8">
          <PhotoPanel
            profile={profile}
            uploading={uploading}
            onUpload={handleUpload}
            onRemove={removePhoto}
            onMakePrimary={makePrimary}
            fileRef={fileRef}
          />
        </TabsContent>

        <TabsContent value="basic" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Full name"><Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="field-name" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Date of birth"><Input type="date" value={form.dob || ""} onChange={(e) => upd("dob", e.target.value)} data-testid="field-dob" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Height (cm)"><Input type="number" value={form.height_cm} onChange={(e) => upd("height_cm", e.target.value)} data-testid="field-height" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Marital status">
            <Select value={form.marital_status || ""} onValueChange={(v) => upd("marital_status", v)}>
              <SelectTrigger data-testid="field-marital" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="never_married">Never married</SelectItem>
                <SelectItem value="divorced">Divorced (post-iddat)</SelectItem>
                <SelectItem value="widowed">Widowed</SelectItem>
                <SelectItem value="awaiting_divorce">Awaiting divorce / talaq</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Mother tongue">
            <Select value={form.mother_tongue || ""} onValueChange={(v) => upd("mother_tongue", v)}>
              <SelectTrigger data-testid="field-mtongue" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{MOTHER_TONGUES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Diet">
            <Select value={form.diet || ""} onValueChange={(v) => upd("diet", v)}>
              <SelectTrigger data-testid="field-diet" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{DIET.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Country"><Input value={form.country} onChange={(e) => upd("country", e.target.value)} data-testid="field-country" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="State"><Input value={form.state} onChange={(e) => upd("state", e.target.value)} data-testid="field-state" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="City"><Input value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="field-city" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="About me" className="md:col-span-2">
            <Textarea rows={4} value={form.about_me} onChange={(e) => upd("about_me", e.target.value)} data-testid="field-about" className="rounded-none border-brand-line" placeholder="Share a little about your deen, aspirations, and family in your own words." />
          </Field>
        </TabsContent>

        <TabsContent value="deen" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Sect">
            <Select value={form.community || ""} onValueChange={(v) => upd("community", v)}>
              <SelectTrigger data-testid="field-sect" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Sub-sect / Maslak"><Input value={form.sub_sect} onChange={(e) => upd("sub_sect", e.target.value)} data-testid="field-subsect" className="h-12 rounded-none border-brand-line" placeholder="Deobandi / Barelvi / Ahl-e-Hadith / Twelver…" /></Field>
          <Field label="Salah / Namaz">
            <Select value={form.namaz || ""} onValueChange={(v) => upd("namaz", v)}>
              <SelectTrigger data-testid="field-namaz" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{NAMAZ.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Hijab / Beard">
            <Select value={form.observance || ""} onValueChange={(v) => upd("observance", v)}>
              <SelectTrigger data-testid="field-observance" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="observant">Observant</SelectItem>
                <SelectItem value="modest">Modest</SelectItem>
                <SelectItem value="not_now">Not currently</SelectItem>
                <SelectItem value="open">Open to discuss</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reads Quran">
            <Select value={form.reads_quran || ""} onValueChange={(v) => upd("reads_quran", v)}>
              <SelectTrigger data-testid="field-quran" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hafiz">Hafiz / Hafiza</SelectItem>
                <SelectItem value="fluent">Fluent</SelectItem>
                <SelectItem value="learning">Learning</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </TabsContent>

        <TabsContent value="family" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Family type"><Input value={form.family_type} onChange={(e) => upd("family_type", e.target.value)} data-testid="field-family-type" className="h-12 rounded-none border-brand-line" placeholder="Nuclear / Joint" /></Field>
          <Field label="Family status"><Input value={form.family_status} onChange={(e) => upd("family_status", e.target.value)} data-testid="field-family-status" className="h-12 rounded-none border-brand-line" placeholder="Middle / Upper-middle / Affluent" /></Field>
          <Field label="Father's occupation"><Input value={form.father_occupation} onChange={(e) => upd("father_occupation", e.target.value)} data-testid="field-father" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Mother's occupation"><Input value={form.mother_occupation} onChange={(e) => upd("mother_occupation", e.target.value)} data-testid="field-mother" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Siblings" className="md:col-span-2"><Textarea rows={2} value={form.siblings} onChange={(e) => upd("siblings", e.target.value)} data-testid="field-siblings" className="rounded-none border-brand-line" /></Field>
        </TabsContent>

        <TabsContent value="career" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Education"><Input value={form.education} onChange={(e) => upd("education", e.target.value)} data-testid="field-education" className="h-12 rounded-none border-brand-line" placeholder="M.B.B.S., B.Tech, Alim/Alima…" /></Field>
          <Field label="Profession"><Input value={form.profession} onChange={(e) => upd("profession", e.target.value)} data-testid="field-profession" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Annual income"><Input value={form.annual_income} onChange={(e) => upd("annual_income", e.target.value)} data-testid="field-income" className="h-12 rounded-none border-brand-line" placeholder="₹ 25 LPA / $150k" /></Field>
        </TabsContent>

        <TabsContent value="partner" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Age (min)"><Input type="number" value={form.partner_age_min} onChange={(e) => upd("partner_age_min", e.target.value)} data-testid="field-p-age-min" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Age (max)"><Input type="number" value={form.partner_age_max} onChange={(e) => upd("partner_age_max", e.target.value)} data-testid="field-p-age-max" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Height (min cm)"><Input type="number" value={form.partner_height_min_cm} onChange={(e) => upd("partner_height_min_cm", e.target.value)} data-testid="field-p-height" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Preferred education"><Input value={form.partner_education} onChange={(e) => upd("partner_education", e.target.value)} data-testid="field-p-education" className="h-12 rounded-none border-brand-line" /></Field>
          <Field label="Preferred sect">
            <Select value={form.partner_community || ""} onValueChange={(v) => upd("partner_community", v)}>
              <SelectTrigger data-testid="field-p-sect" className="h-12 rounded-none border-brand-line"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Any">Any</SelectItem>
                {SECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Expectations" className="md:col-span-2"><Textarea rows={4} value={form.partner_expectations} onChange={(e) => upd("partner_expectations", e.target.value)} data-testid="field-p-expect" className="rounded-none border-brand-line" placeholder="Deen-conscious, family-oriented…" /></Field>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="label-eyebrow text-brand-muted">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function PhotoPanel({ profile, uploading, onUpload, onRemove, onMakePrimary, fileRef }) {
  const photos = profile?.photos || [];
  const primaryId = profile?.primary_photo?.id;
  const hasAny = photos.length > 0;

  const onDrop = (e) => {
    e.preventDefault();
    onUpload(e.dataTransfer.files);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="photo-panel">
      {/* Left: Primary photo showcase / upload zone */}
      <div className="lg:col-span-5">
        <div className="label-eyebrow text-brand-terracotta">Primary photograph</div>
        <p className="mt-2 text-sm text-brand-muted">
          Every dossier requires one primary photo — visible on your profile card, browse listings, and interest requests.
        </p>

        <div
          className="mt-6 aspect-square bg-brand-bone border border-brand-line relative overflow-hidden group"
          data-testid="primary-photo-showcase"
        >
          {profile?.primary_photo ? (
            <img
              src={photoUrl(profile.primary_photo)}
              alt="Your primary photo"
              className="w-full h-full object-cover"
              data-testid="primary-photo-img"
            />
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="w-full h-full flex flex-col items-center justify-center gap-3 text-brand-muted hover:bg-brand-line/30 transition-colors"
              data-testid="primary-upload-empty"
            >
              <div className="w-20 h-20 rounded-full bg-white border border-brand-line flex items-center justify-center">
                <Camera size={32} weight="duotone" className="text-brand-terracotta" />
              </div>
              <div className="text-sm font-serif text-brand-ink">Add your photograph</div>
              <div className="text-[11px] uppercase tracking-widest">Click or drop an image here</div>
              <div className="text-[10px] text-brand-muted mt-2">JPG · PNG · WebP · max 8MB</div>
            </button>
          )}

          {profile?.primary_photo && (
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-xs uppercase tracking-widest text-white px-3 py-1.5 bg-brand-terracotta hover:bg-brand-terracotta/90 rounded-full"
                data-testid="primary-photo-replace"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={() => onRemove(profile.primary_photo.id)}
                className="text-xs uppercase tracking-widest text-white/80 hover:text-white"
                data-testid="primary-photo-remove"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: gallery + guidelines */}
      <div className="lg:col-span-7">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow text-brand-muted">Photo gallery</div>
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="photo-add-btn"
            className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white"
            size="sm"
          >
            <Plus size={14} className="mr-1" /> {uploading ? "Uploading…" : "Add photo"}
          </Button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={(e) => onUpload(e.target.files)}
          className="hidden"
          data-testid="photo-file-input"
        />

        {hasAny ? (
          <div className="mt-4 grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-gallery">
            {photos.map((ph) => {
              const isPrimary = ph.id === primaryId;
              return (
                <div
                  key={ph.id}
                  className={`relative aspect-square bg-brand-bone border ${isPrimary ? "border-brand-terracotta ring-2 ring-brand-terracotta/30" : "border-brand-line"} overflow-hidden group`}
                  data-testid={`gallery-photo-${ph.id}`}
                >
                  <img src={photoUrl(ph)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  {isPrimary && (
                    <div className="absolute top-2 left-2 bg-brand-terracotta text-white text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Star size={9} weight="fill" /> Primary
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-1">
                    {!isPrimary && (
                      <button
                        type="button"
                        onClick={() => onMakePrimary(ph.id)}
                        title="Set as primary"
                        data-testid={`gallery-primary-${ph.id}`}
                        className="w-7 h-7 rounded-full bg-white/90 hover:bg-white text-brand-ink flex items-center justify-center"
                      >
                        <Star size={12} weight="bold" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemove(ph.id)}
                      title="Remove"
                      data-testid={`gallery-remove-${ph.id}`}
                      className="w-7 h-7 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-red-600 flex items-center justify-center"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 border border-dashed border-brand-line p-10 text-center text-brand-muted text-sm">
            No additional photos yet. You can add up to 5 photos to your gallery.
          </div>
        )}

        {/* Guidelines */}
        <div className="mt-8 bg-brand-bone border-l-2 border-brand-gold p-5">
          <div className="label-eyebrow text-brand-terracotta">Adab · Photo guidelines</div>
          <ul className="mt-3 text-xs text-brand-ink space-y-2 leading-relaxed">
            <li>· Clear, well-lit photographs of yourself — face visible.</li>
            <li>· Modest attire, no group shots without permission.</li>
            <li>· Family members may appear only with their consent.</li>
            <li>· No filters that materially alter your appearance.</li>
            <li>· Photos flagged by other members may be reviewed and removed.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
